import { HABITAT_TRAIT_NAMES, initializeHabitatTraits } from './habitat-traits';
import type { HabitatSystem } from './habitat';
import { BehaviorMode } from './behavior-mode';
import { defaultRecipe, type MatterTransfers } from './biomass';

export const enum EntityKind {
  Inactive = 0,
  Algae = 1,
  Herbivore = 2,
  Predator = 3,
  Star = 4,
  CelestialBody = 5,
  /** @deprecated Runtime bodies use CelestialBody. */
  Planet = CelestialBody,
  /** @deprecated Retained only for older serialized fixtures. */
  Moon = 6,
  Corpse = 7,
  Scavenger = 8,
  Comet = 9,
  Asteroid = 10,
}

export interface PrimaryStateView {
  readonly count: number;
  readonly x: Float64Array;
  readonly y: Float64Array;
  readonly vx: Float64Array;
  readonly vy: Float64Array;
  readonly mass: Float64Array;
  readonly energy: Float64Array;
  readonly age: Float64Array;
  readonly kind: Uint8Array;
  readonly generation: Uint16Array;
}

/**
 * Compact structure-of-arrays storage for every fully simulated entity.
 * Removal uses swap-with-last so active data stays tightly packed.
 */
export class PrimaryStore implements PrimaryStateView {
  public habitat: HabitatSystem | undefined;
  public readonly thermalOptimum: Float32Array;
  public readonly thermalBreadth: Float32Array;
  public readonly pressureOptimum: Float32Array;
  public readonly pressureBreadth: Float32Array;
  public count = 0;
  public reservedSlots = 0;
  public matter: MatterTransfers | undefined;
  public readonly biomassRecipe: Uint8Array;
  public readonly capacity: number;
  public readonly x: Float64Array;
  public readonly y: Float64Array;
  public readonly vx: Float64Array;
  public readonly vy: Float64Array;
  public readonly ax: Float64Array;
  public readonly ay: Float64Array;
  public readonly mass: Float64Array;
  public readonly energy: Float64Array;
  public readonly age: Float64Array;
  public readonly kind: Uint8Array;
  public readonly generation: Uint16Array;
  public readonly cooldown: Float64Array;
  public readonly attackCooldown: Float64Array;
  public readonly lineage: Uint32Array;
  public readonly behavior: Uint8Array;
  public readonly behaviorTarget: Uint32Array;
  public readonly behaviorTimer: Float64Array;
  public readonly migrationOrigin: Uint8Array;
  public readonly migrationCaptureTime: Float64Array;
  public readonly memoryBody: Int16Array;
  public readonly memoryMode: Uint8Array;
  public readonly memoryVx: Float64Array;
  public readonly memoryVy: Float64Array;
  public readonly memoryAge: Float64Array;
  public readonly memoryX: Float64Array;
  public readonly memoryY: Float64Array;
  public readonly memoryTimer: Float64Array;
  public readonly scarcity: Float64Array;
  public readonly satiety: Float64Array;
  public readonly handlingTimer: Float64Array;
  public readonly feedingHistory: Float64Array;
  public readonly resourceMemory: Float64Array;
  public readonly resourceTrend: Float64Array;
  public readonly foragingSuccess: Float64Array;
  public readonly dispersalReadiness: Float64Array;
  public readonly carrionClaimant: Uint32Array;
  public readonly carrionClaimTimer: Float64Array;
  public readonly rejectedTarget: Int32Array;
  public readonly rejectedTargetTimer: Float64Array;
  public readonly pursuitCost: Float64Array;
  public readonly pursuitStallTime: Float64Array;
  private readonly compactionMap: Int32Array;

  public constructor(capacity: number) {
    if (!Number.isInteger(capacity) || capacity <= 0) {
      throw new RangeError('PrimaryStore capacity must be a positive integer.');
    }

    this.capacity = capacity;
    this.thermalOptimum = new Float32Array(capacity);
    this.thermalBreadth = new Float32Array(capacity);
    this.pressureOptimum = new Float32Array(capacity);
    this.pressureBreadth = new Float32Array(capacity);

    this.x = new Float64Array(capacity);
    this.y = new Float64Array(capacity);
    this.vx = new Float64Array(capacity);
    this.vy = new Float64Array(capacity);
    this.ax = new Float64Array(capacity);
    this.ay = new Float64Array(capacity);
    this.mass = new Float64Array(capacity);
    this.biomassRecipe = new Uint8Array(capacity);
    this.energy = new Float64Array(capacity);
    this.age = new Float64Array(capacity);
    this.kind = new Uint8Array(capacity);
    this.generation = new Uint16Array(capacity);
    this.cooldown = new Float64Array(capacity);
    this.attackCooldown = new Float64Array(capacity);
    this.lineage = new Uint32Array(capacity);
    this.behavior = new Uint8Array(capacity);
    this.behaviorTarget = new Uint32Array(capacity);
    this.behaviorTimer = new Float64Array(capacity);
    this.migrationOrigin = new Uint8Array(capacity);
    this.migrationCaptureTime = new Float64Array(capacity);
    this.memoryBody = new Int16Array(capacity).fill(-1);
    this.memoryMode = new Uint8Array(capacity);
    this.memoryVx = new Float64Array(capacity);
    this.memoryVy = new Float64Array(capacity);
    this.memoryAge = new Float64Array(capacity);
    this.memoryX = new Float64Array(capacity);
    this.memoryY = new Float64Array(capacity);
    this.memoryTimer = new Float64Array(capacity);
    this.scarcity = new Float64Array(capacity);
    this.satiety = new Float64Array(capacity);
    this.handlingTimer = new Float64Array(capacity);
    this.feedingHistory = new Float64Array(capacity);
    this.resourceMemory = new Float64Array(capacity);
    this.resourceTrend = new Float64Array(capacity);
    this.foragingSuccess = new Float64Array(capacity);
    this.dispersalReadiness = new Float64Array(capacity);
    this.carrionClaimant = new Uint32Array(capacity);
    this.carrionClaimTimer = new Float64Array(capacity);
    this.rejectedTarget = new Int32Array(capacity).fill(-1);
    this.rejectedTargetTimer = new Float64Array(capacity);
    this.pursuitCost = new Float64Array(capacity);
    this.pursuitStallTime = new Float64Array(capacity);
    this.compactionMap = new Int32Array(capacity);
  }

  public add(
    kind: EntityKind,
    x: number,
    y: number,
    mass: number,
    energy: number,
    vx = 0,
    vy = 0,
    generation = 0,
    lineage = 0,
  ): number {
    if (this.count >= this.capacity - this.reservedSlots) {
      return -1;
    }

    const index = this.count;
    this.count += 1;
    this.kind[index] = kind;
    this.biomassRecipe[index] = defaultRecipe(kind);
    this.x[index] = x;
    this.y[index] = y;
    this.vx[index] = vx;
    this.vy[index] = vy;
    this.ax[index] = 0;
    this.ay[index] = 0;
    this.mass[index] = mass;
    this.energy[index] = energy;
    this.age[index] = 0;
    this.generation[index] = generation;
    this.cooldown[index] = 0;
    this.attackCooldown[index] = 0;
    this.lineage[index] = lineage >>> 0;
    this.behavior[index] = 0;
    this.behaviorTarget[index] = 0;
    this.behaviorTimer[index] = 0;
    this.migrationOrigin[index] = 255;
    this.migrationCaptureTime[index] = 0;
    this.memoryBody[index] = -1; this.memoryMode[index] = 0;
    this.memoryVx[index] = 0; this.memoryVy[index] = 0; this.memoryAge[index] = 0;
    this.memoryX[index] = x;
    this.memoryY[index] = y;
    this.memoryTimer[index] = 0;
    this.scarcity[index] = 0;
    this.satiety[index] = 0;
    this.handlingTimer[index] = 0;
    this.feedingHistory[index] = 0;
    this.resourceMemory[index] = 0;
    this.resourceTrend[index] = 0;
    this.foragingSuccess[index] = 0;
    this.dispersalReadiness[index] = 0;
    this.carrionClaimant[index] = 0;
    this.carrionClaimTimer[index] = 0;
    this.rejectedTarget[index] = -1;
    this.rejectedTargetTimer[index] = 0;
    this.pursuitCost[index] = 0;
    this.pursuitStallTime[index] = 0;
    initializeHabitatTraits(this, index);
    return index;
  }

  public remove(index: number): void {
    if (!Number.isInteger(index) || index < 0 || index >= this.count) {
      throw new RangeError(`PrimaryStore index ${index} is out of bounds.`);
    }

    const last = this.count - 1;
    this.matter?.discard(index, 'removed');
    if (index !== last) {
      this.copy(last, index);
    }
    this.clear(last);
    this.count = last;
    for (let entity = 0; entity < this.count; entity += 1) {
      if (this.rejectedTarget[entity] === index) {
        this.rejectedTarget[entity] = -1;
        this.rejectedTargetTimer[entity] = 0;
      } else if (this.rejectedTarget[entity] === last) {
        this.rejectedTarget[entity] = index;
      }
      if (this.behavior[entity] === BehaviorMode.Migrate) continue;
      const target = this.behaviorTarget[entity];
      if (target === index) {
        this.behaviorTarget[entity] = 0;
        this.behaviorTimer[entity] = 0;
      } else if (target === last) {
        this.behaviorTarget[entity] = index;
      }
    }
  }

  public reset(): void {
    this.matter = undefined;
    this.habitat = undefined;
    this.memoryBody.fill(-1); this.memoryMode.fill(0); this.memoryAge.fill(0);
    this.memoryVx.fill(0); this.memoryVy.fill(0); this.memoryTimer.fill(0);
    for (const name of HABITAT_TRAIT_NAMES) this[name].fill(0);
    this.biomassRecipe.fill(0);
    this.reservedSlots = 0;
    this.kind.fill(EntityKind.Inactive, 0, this.count);
    this.count = 0;
  }

  public compactInactive(): number {
    const previousCount = this.count;
    this.compactionMap.fill(-1, 0, previousCount);
    let write = 0;
    for (let read = 0; read < previousCount; read += 1) {
      if ((this.kind[read] ?? EntityKind.Inactive) === EntityKind.Inactive) {
        this.matter?.discard(read, 'removed');
        continue;
      }
      this.compactionMap[read] = write;
      if (write !== read) {
        this.copy(read, write);
      }
      write += 1;
    }

    // Migration destinations are registry slots; pursuit/feeding targets are
    // dense entity indices. Only the latter move during compaction.
    for (let index = 0; index < write; index += 1) {
      this.rejectedTarget[index] = this.compactionMap[this.rejectedTarget[index] ?? -1] ?? -1;
      if (this.rejectedTarget[index]! < 0) this.rejectedTargetTimer[index] = 0;
      if (this.behavior[index] === BehaviorMode.Migrate) {
        continue;
      }
      const oldTarget = this.behaviorTarget[index] ?? 0;
      if (oldTarget >= previousCount) {
        continue;
      }
      const newTarget = this.compactionMap[oldTarget] ?? -1;
      if (newTarget >= 0) {
        this.behaviorTarget[index] = newTarget;
      } else {
        this.behaviorTarget[index] = 0;
        this.behaviorTimer[index] = 0;
      }
    }
    for (let index = write; index < previousCount; index += 1) {
      this.clear(index);
    }
    this.count = write;
    return previousCount - write;
  }

  public isFinite(index: number): boolean {
    return (
      HABITAT_TRAIT_NAMES.every(name => Number.isFinite(this[name][index])) &&
      (this.thermalBreadth[index] ?? 0) > 0 &&
      (this.pressureBreadth[index] ?? 0) > 0 &&
      Number.isFinite(this.x[index]) &&
      Number.isFinite(this.y[index]) &&
      Number.isFinite(this.vx[index]) &&
      Number.isFinite(this.vy[index]) &&
      Number.isFinite(this.mass[index]) &&
      Number.isFinite(this.energy[index]) &&
      (this.mass[index] ?? 0) > 0
    );
  }

  private copy(source: number, target: number): void {
    for (const name of HABITAT_TRAIT_NAMES) this[name][target] = this[name][source]!;
    this.kind[target] = this.kind[source] ?? EntityKind.Inactive;
    this.biomassRecipe[target] = this.biomassRecipe[source] ?? 0;
    this.x[target] = this.x[source] ?? 0;
    this.y[target] = this.y[source] ?? 0;
    this.vx[target] = this.vx[source] ?? 0;
    this.vy[target] = this.vy[source] ?? 0;
    this.ax[target] = this.ax[source] ?? 0;
    this.ay[target] = this.ay[source] ?? 0;
    this.mass[target] = this.mass[source] ?? 0;
    this.energy[target] = this.energy[source] ?? 0;
    this.age[target] = this.age[source] ?? 0;
    this.generation[target] = this.generation[source] ?? 0;
    this.cooldown[target] = this.cooldown[source] ?? 0;
    this.attackCooldown[target] = this.attackCooldown[source] ?? 0;
    this.lineage[target] = this.lineage[source] ?? 0;
    this.behavior[target] = this.behavior[source] ?? 0;
    this.behaviorTarget[target] = this.behaviorTarget[source] ?? 0;
    this.behaviorTimer[target] = this.behaviorTimer[source] ?? 0;
    this.migrationOrigin[target] = this.migrationOrigin[source] ?? 255;
    this.migrationCaptureTime[target] = this.migrationCaptureTime[source] ?? 0;
    this.memoryBody[target] = this.memoryBody[source]!;
    this.memoryMode[target] = this.memoryMode[source]!;
    this.memoryVx[target] = this.memoryVx[source]!;
    this.memoryVy[target] = this.memoryVy[source]!;
    this.memoryAge[target] = this.memoryAge[source]!;
    this.memoryX[target] = this.memoryX[source] ?? 0;
    this.memoryY[target] = this.memoryY[source] ?? 0;
    this.memoryTimer[target] = this.memoryTimer[source] ?? 0;
    this.scarcity[target] = this.scarcity[source] ?? 0;
    this.satiety[target] = this.satiety[source] ?? 0;
    this.handlingTimer[target] = this.handlingTimer[source] ?? 0;
    this.feedingHistory[target] = this.feedingHistory[source] ?? 0;
    this.resourceMemory[target] = this.resourceMemory[source] ?? 0;
    this.resourceTrend[target] = this.resourceTrend[source] ?? 0;
    this.foragingSuccess[target] = this.foragingSuccess[source] ?? 0;
    this.dispersalReadiness[target] = this.dispersalReadiness[source] ?? 0;
    this.carrionClaimant[target] = this.carrionClaimant[source] ?? 0;
    this.carrionClaimTimer[target] = this.carrionClaimTimer[source] ?? 0;
    this.rejectedTarget[target] = this.rejectedTarget[source] ?? -1;
    this.rejectedTargetTimer[target] = this.rejectedTargetTimer[source] ?? 0;
    this.pursuitCost[target] = this.pursuitCost[source] ?? 0;
    this.pursuitStallTime[target] = this.pursuitStallTime[source] ?? 0;
  }

  private clear(index: number): void {
    for (const name of HABITAT_TRAIT_NAMES) this[name][index] = 0;
    this.biomassRecipe[index] = 0;
    this.kind[index] = EntityKind.Inactive;
    this.x[index] = 0;
    this.y[index] = 0;
    this.vx[index] = 0;
    this.vy[index] = 0;
    this.ax[index] = 0;
    this.ay[index] = 0;
    this.mass[index] = 0;
    this.energy[index] = 0;
    this.age[index] = 0;
    this.generation[index] = 0;
    this.cooldown[index] = 0;
    this.attackCooldown[index] = 0;
    this.lineage[index] = 0;
    this.behavior[index] = 0;
    this.behaviorTarget[index] = 0;
    this.behaviorTimer[index] = 0;
    this.migrationOrigin[index] = 255;
    this.migrationCaptureTime[index] = 0;
    this.memoryBody[index] = -1; this.memoryMode[index] = 0;
    this.memoryVx[index] = 0; this.memoryVy[index] = 0; this.memoryAge[index] = 0;
    this.memoryX[index] = 0;
    this.memoryY[index] = 0;
    this.memoryTimer[index] = 0;
    this.scarcity[index] = 0;
    this.satiety[index] = 0;
    this.handlingTimer[index] = 0;
    this.feedingHistory[index] = 0;
    this.resourceMemory[index] = 0;
    this.resourceTrend[index] = 0;
    this.foragingSuccess[index] = 0;
    this.dispersalReadiness[index] = 0;
    this.carrionClaimant[index] = 0;
    this.carrionClaimTimer[index] = 0;
    this.rejectedTarget[index] = -1;
    this.rejectedTargetTimer[index] = 0;
    this.pursuitCost[index] = 0;
    this.pursuitStallTime[index] = 0;
  }
}
